import type { JSX } from "react";
import type { Turn, Action, TurnOutput } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";
import { getFeedbackColor, getFeedbackIcon } from "./turnDetailShared";

interface TurnHeaderProps {
  turn: Turn;
  actions: Action[];
  outputs: TurnOutput[];
  apiError: string | null;
}

function TurnStats({ turn, actions, outputs }: Omit<TurnHeaderProps, "apiError">): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-500">Turn ID</label>
        <p className="mt-1 text-sm font-mono text-gray-900">{turn.turn_id}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-500">Created At</label>
        <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(turn.created_at)}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-500">Actions</label>
        <p className="mt-1 text-sm text-gray-900">
          {actions.length}
          {actions.some((a) => a.is_decision) && (
            <span className="ml-2 text-xs text-purple-600" title="Includes agent decision entries">
              (incl. decisions)
            </span>
          )}
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-500">Outputs</label>
        <p className="mt-1 text-sm text-gray-900">{outputs.length}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-500">Turn Cost</label>
        <div className="mt-1">
          <p className="text-sm font-medium text-gray-900">
            {formatCost(getTotalCostFromApi(turn.total_input_cost_usd, turn.total_output_cost_usd))}
          </p>
          {(turn.total_prompt_tokens || turn.total_completion_tokens) && (
            <p className="text-xs text-gray-500">
              {((turn.total_prompt_tokens || 0) + (turn.total_completion_tokens || 0)).toLocaleString()}{" "}
              tokens
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TurnHeader({ turn, actions, outputs, apiError }: TurnHeaderProps): JSX.Element {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Turn Details</h2>
        {apiError && (
          <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
            Using mock data - API not available
          </div>
        )}
      </div>

      <TurnStats turn={turn} actions={actions} outputs={outputs} />

      <div className="mt-4 flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-500">User Feedback:</span>
          <span className={`text-lg ${getFeedbackColor(turn.thumbs_up, turn.thumbs_down)}`}>
            {getFeedbackIcon(turn.thumbs_up, turn.thumbs_down)}
          </span>
        </div>
        {turn.feedback_comment && (
          <div className="flex-1">
            <span className="text-sm text-gray-600">"{turn.feedback_comment}"</span>
          </div>
        )}
      </div>

      {turn.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <span className="text-red-600 mr-2">⚠️</span>
            <span className="text-sm font-medium text-red-800">
              This turn encountered an error during processing
            </span>
          </div>
          {turn.error_text && <p className="mt-2 text-sm text-red-700">{turn.error_text}</p>}
        </div>
      )}
    </div>
  );
}
